import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Dashboard,
  People,
  Assessment,
  Settings,
  School,
  Timeline,
  FolderOpen,
  Description,
  LocalHospital,
  Analytics,
  AdminPanelSettings,
} from '@mui/icons-material'

// ============================================================
// THEME DEFINITIONS
// ============================================================

// Current theme (default MUI with minor tweaks)
const currentTheme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
    background: { default: '#f5f5f5', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
})

// Option A: Professional Dense - Higher information density, muted colors
const themeProfessionalDense = createTheme({
  palette: {
    primary: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8' },
    secondary: { main: '#64748b' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#1e293b', secondary: '#64748b' },
    divider: '#e2e8f0',
    action: {
      hover: '#f1f5f9',
      selected: '#e2e8f0',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 13,
    h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontSize: '1rem', fontWeight: 600 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 6 },
  components: {
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, padding: '6px 12px' },
        contained: { boxShadow: 'none' },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
      styleOverrides: {
        root: { '& .MuiInputBase-root': { fontSize: '0.875rem' } },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.8125rem' } },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: { height: 22, fontSize: '0.75rem', fontWeight: 500 },
        sizeSmall: { height: 20 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '8px 12px', fontSize: '0.8125rem' },
        head: { fontWeight: 600, backgroundColor: '#f8fafc', color: '#475569' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid #e2e8f0',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { padding: '6px 12px', borderRadius: 4 },
      },
    },
  },
})

// Option B: Clinical Clean - Minimal, flat design inspired by EMR systems
const themeClinicalClean = createTheme({
  palette: {
    primary: { main: '#0f766e', light: '#14b8a6', dark: '#115e59' },
    secondary: { main: '#71717a' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#18181b', secondary: '#71717a' },
    divider: '#e4e4e7',
    success: { main: '#16a34a', light: '#dcfce7' },
    warning: { main: '#ca8a04', light: '#fef9c3' },
    error: { main: '#dc2626', light: '#fee2e2' },
  },
  typography: {
    fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 13,
    h4: { fontSize: '1.375rem', fontWeight: 600 },
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '0.9375rem', fontWeight: 600 },
    subtitle1: { fontSize: '0.8125rem', fontWeight: 500 },
    body1: { fontSize: '0.8125rem', lineHeight: 1.5 },
    body2: { fontSize: '0.75rem' },
  },
  shape: { borderRadius: 4 },
  components: {
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, minHeight: 32 },
        outlined: { borderWidth: 1.5 },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: '#d4d4d8' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 20,
          fontSize: '0.6875rem',
          fontWeight: 500,
          borderRadius: 4,
        },
        colorSuccess: { backgroundColor: '#dcfce7', color: '#166534' },
        colorWarning: { backgroundColor: '#fef9c3', color: '#854d0e' },
        colorError: { backgroundColor: '#fee2e2', color: '#991b1b' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '10px 16px', fontSize: '0.8125rem', borderColor: '#e4e4e7' },
        head: { fontWeight: 600, color: '#52525b', backgroundColor: '#fafafa' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: 'none', border: '1px solid #e4e4e7' },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#e4e4e7' },
      },
    },
  },
})

// ============================================================
// SAMPLE DATA
// ============================================================

const sampleInterventions = [
  { id: 1, code: 'BCS_001', name: 'Breast Cancer Support', status: 'active', pathways: 8, enrolled: 124 },
  { id: 2, code: 'LUNG_RESILIENCE', name: 'Lung Cancer Resilience', status: 'draft', pathways: 5, enrolled: 0 },
  { id: 3, code: 'CHEMO_WELLBEING', name: 'Chemotherapy Wellbeing', status: 'active', pathways: 12, enrolled: 89 },
]

const samplePathways = [
  { id: 1, code: 'stress_management', name: 'Stress Management Basics', type: 'education', domain: 'Emotional Wellness', status: 'active' },
  { id: 2, code: 'getting_to_know_you', name: 'Getting To Know You', type: 'activity', domain: 'General Health', status: 'active' },
  { id: 3, code: 'breathing_exercises', name: 'Breathing Exercises', type: 'skill_building', domain: 'Stress Management', status: 'draft' },
]

// ============================================================
// CURRENT DESIGN COMPONENTS (showing what exists now)
// ============================================================

function CurrentDesignSample() {
  const [sectionOpen, setSectionOpen] = useState({ overview: true, pathways: false })

  return (
    <Box>
      {/* Header with large title and colorful button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Intervention Programs</Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          New Intervention
        </Button>
      </Box>

      {/* Data table with colorful chips */}
      <Paper sx={{ mb: 3, p: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pathways</TableCell>
              <TableCell>Enrolled</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sampleInterventions.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  <Chip
                    label={row.status}
                    color={row.status === 'active' ? 'success' : row.status === 'draft' ? 'default' : 'error'}
                  />
                </TableCell>
                <TableCell>{row.pathways}</TableCell>
                <TableCell>{row.enrolled}</TableCell>
                <TableCell>
                  <IconButton size="small"><EditIcon /></IconButton>
                  <IconButton size="small"><CopyIcon /></IconButton>
                  <IconButton size="small" color="error"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Accordion-style editor */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Breast Cancer Support</Typography>
          <Chip label="Active" color="success" sx={{ mr: 1 }} />
          <Button variant="contained" startIcon={<EditIcon />}>Save</Button>
        </Box>

        {/* Collapsible section */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer', py: 1 }}
          onClick={() => setSectionOpen(s => ({ ...s, overview: !s.overview }))}
        >
          <IconButton size="small">
            {sectionOpen.overview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle1" fontWeight="medium">Overview</Typography>
        </Box>
        <Collapse in={sectionOpen.overview}>
          <Box sx={{ pl: 5, pr: 2, pb: 2 }}>
            <TextField label="Code" fullWidth sx={{ mb: 2 }} value="BCS_001" />
            <TextField label="Name" fullWidth sx={{ mb: 2 }} value="Breast Cancer Support" />
            <TextField label="Description" fullWidth multiline rows={3} sx={{ mb: 2 }} />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value="active" label="Status">
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>
        <Divider />

        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer', py: 1 }}
          onClick={() => setSectionOpen(s => ({ ...s, pathways: !s.pathways }))}
        >
          <IconButton size="small">
            {sectionOpen.pathways ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="subtitle1" fontWeight="medium">Pathways</Typography>
        </Box>
        <Collapse in={sectionOpen.pathways}>
          <Box sx={{ pl: 5, pr: 2, pb: 2 }}>
            <Typography color="text.secondary">8 pathways assigned</Typography>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  )
}

// ============================================================
// PROFESSIONAL DENSE DESIGN (Option A)
// ============================================================

function ProfessionalDenseSample() {
  return (
    <Box>
      {/* Compact header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h5">Intervention Programs</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage clinical intervention configurations
          </Typography>
        </Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />}>
          New Program
        </Button>
      </Box>

      {/* Dense data table with subtle status indicators */}
      <Paper sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Pathways</TableCell>
              <TableCell align="right">Enrolled</TableCell>
              <TableCell width={100}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sampleInterventions.map((row) => (
              <TableRow key={row.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem" color="text.secondary">
                    {row.code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
                </TableCell>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.75rem',
                      color: row.status === 'active' ? '#16a34a' : '#71717a',
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: row.status === 'active' ? '#16a34a' : '#a1a1aa',
                      }}
                    />
                    {row.status === 'active' ? 'Active' : 'Draft'}
                  </Box>
                </TableCell>
                <TableCell align="right">{row.pathways}</TableCell>
                <TableCell align="right">{row.enrolled}</TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <IconButton size="small" sx={{ p: 0.5 }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" sx={{ p: 0.5 }}><CopyIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Inline expanded form - no accordions, sections always visible */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography variant="h6">Breast Cancer Support</Typography>
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: '#dcfce7',
                color: '#166534',
                fontSize: '0.6875rem',
                fontWeight: 500,
                borderRadius: 1,
              }}
            >
              Active
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="outlined" size="small">Cancel</Button>
            <Button variant="contained" size="small">Save Changes</Button>
          </Box>
        </Box>

        {/* Inline sections with subtle dividers */}
        <Box display="flex" flexDirection="column" gap={2.5}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
              Basic Information
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField label="Code" value="BCS_001" disabled size="small" />
              <TextField label="Name" value="Breast Cancer Support" size="small" />
            </Box>
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              size="small"
              sx={{ mt: 2 }}
              placeholder="Brief description of this intervention..."
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
              Configuration
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
              <FormControl size="small">
                <InputLabel>Status</InputLabel>
                <Select value="active" label="Status">
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Target Population" size="small" placeholder="e.g., Stage II-III" />
              <TextField label="Duration (weeks)" size="small" type="number" />
            </Box>
          </Box>

          <Divider />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                Assigned Pathways
              </Typography>
              <Button size="small" startIcon={<AddIcon />}>Add Pathway</Button>
            </Box>
            <Table size="small">
              <TableBody>
                {samplePathways.slice(0, 2).map((p) => (
                  <TableRow key={p.id}>
                    <TableCell sx={{ pl: 0 }}>
                      <Typography variant="body2" fontWeight={500}>{p.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.type}</Typography>
                    </TableCell>
                    <TableCell>{p.domain}</TableCell>
                    <TableCell width={40}>
                      <IconButton size="small" sx={{ p: 0.5 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

// ============================================================
// CLINICAL CLEAN DESIGN (Option B)
// ============================================================

function ClinicalCleanSample() {
  return (
    <Box>
      {/* Minimal header */}
      <Box mb={2.5}>
        <Typography variant="h5" mb={0.5}>Intervention Programs</Typography>
        <Typography variant="body2" color="text.secondary">
          3 programs · 2 active
        </Typography>
      </Box>

      {/* Clean card-style list */}
      <Box display="flex" flexDirection="column" gap={1} mb={2.5}>
        {sampleInterventions.map((row) => (
          <Paper
            key={row.id}
            sx={{
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { bgcolor: '#fafafa' },
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" fontWeight={500}>{row.name}</Typography>
                {row.status === 'active' && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#16a34a',
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {row.code} · {row.pathways} pathways · {row.enrolled} enrolled
              </Typography>
            </Box>
            <IconButton size="small"><EditIcon sx={{ fontSize: 18 }} /></IconButton>
          </Paper>
        ))}
      </Box>

      <Button variant="outlined" fullWidth startIcon={<AddIcon />} sx={{ mb: 3 }}>
        Create New Program
      </Button>

      {/* Clean form layout */}
      <Paper sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Typography variant="h6">Breast Cancer Support</Typography>
          <Box display="flex" gap={1}>
            <Button variant="text" size="small">Discard</Button>
            <Button variant="contained" size="small">Save</Button>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="grid" gridTemplateColumns="120px 1fr" gap={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">Code</Typography>
            <Typography variant="body2" fontFamily="monospace">BCS_001</Typography>

            <Typography variant="body2" color="text.secondary">Name</Typography>
            <TextField size="small" value="Breast Cancer Support" fullWidth variant="outlined" />

            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box display="flex" gap={1}>
              <Chip
                label="Active"
                color="success"
                variant="filled"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                label="Draft"
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">Population</Typography>
            <TextField size="small" placeholder="Target patient population" fullWidth />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">Pathways ({samplePathways.length})</Typography>
              <Button size="small" variant="text">Manage</Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {samplePathways.map((p) => (
                <Chip
                  key={p.id}
                  label={p.name}
                  size="small"
                  variant="outlined"
                  onDelete={() => {}}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

// ============================================================
// NAVIGATION COMPARISON
// ============================================================

function NavigationComparison({ variant }) {
  const navItems = [
    { icon: <Dashboard sx={{ fontSize: 18 }} />, label: 'Dashboard', active: false },
    { icon: <LocalHospital sx={{ fontSize: 18 }} />, label: 'Interventions', active: true },
    { icon: <Timeline sx={{ fontSize: 18 }} />, label: 'Pathways', active: false },
    { icon: <School sx={{ fontSize: 18 }} />, label: 'Education', active: false },
    { icon: <People sx={{ fontSize: 18 }} />, label: 'Patients', active: false },
    { icon: <Analytics sx={{ fontSize: 18 }} />, label: 'Analytics', active: false },
  ]

  if (variant === 'current') {
    return (
      <Box sx={{ width: 200, bgcolor: 'background.paper', borderRight: 1, borderColor: 'divider', p: 1 }}>
        <Typography variant="h6" sx={{ p: 1.5, fontWeight: 'bold' }}>ASTA</Typography>
        <Divider sx={{ mb: 1 }} />
        <List dense>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton selected={item.active} sx={{ borderRadius: 1 }}>
                <Box sx={{ mr: 1.5, display: 'flex', color: item.active ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </Box>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    )
  }

  if (variant === 'dense') {
    return (
      <Box sx={{ width: 180, bgcolor: '#f8fafc', borderRight: 1, borderColor: '#e2e8f0', py: 1.5, px: 1 }}>
        <Typography variant="subtitle1" sx={{ px: 1, mb: 1.5, fontWeight: 600, color: '#1e293b' }}>
          ASTA
        </Typography>
        <List dense disablePadding>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                selected={item.active}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  '&.Mui-selected': { bgcolor: '#e2e8f0' },
                }}
              >
                <Box sx={{ mr: 1, display: 'flex', color: item.active ? '#2563eb' : '#64748b', fontSize: 16 }}>
                  {item.icon}
                </Box>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: item.active ? 500 : 400,
                    color: item.active ? '#1e293b' : '#475569',
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    )
  }

  // Clinical - text only, no icons
  return (
    <Box sx={{ width: 160, bgcolor: '#fff', borderRight: 1, borderColor: '#e4e4e7', py: 2, px: 1 }}>
      <Typography
        variant="body2"
        sx={{ px: 1, mb: 2, fontWeight: 600, color: '#0f766e', letterSpacing: '0.05em' }}
      >
        ASTA
      </Typography>
      <List dense disablePadding>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
            <ListItemButton
              selected={item.active}
              sx={{
                py: 0.5,
                px: 1,
                borderRadius: 1,
                borderLeft: item.active ? '2px solid #0f766e' : '2px solid transparent',
                '&.Mui-selected': { bgcolor: 'transparent' },
              }}
            >
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.8125rem',
                  fontWeight: item.active ? 500 : 400,
                  color: item.active ? '#0f766e' : '#52525b',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

// ============================================================
// MAIN COMPARISON PAGE
// ============================================================

function DesignCompare() {
  const [selectedTheme, setSelectedTheme] = useState('current')

  // Load fonts for comparison
  useEffect(() => {
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=Inter:wght@400;500;600&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
    return () => document.head.removeChild(link)
  }, [])

  const themes = {
    current: { theme: currentTheme, label: 'Current (Default MUI)', Component: CurrentDesignSample, navVariant: 'current' },
    dense: { theme: themeProfessionalDense, label: 'Option A: Professional Dense', Component: ProfessionalDenseSample, navVariant: 'dense' },
    clinical: { theme: themeClinicalClean, label: 'Option B: Clinical Clean', Component: ClinicalCleanSample, navVariant: 'clinical' },
  }

  const selected = themes[selectedTheme]

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Theme selector toolbar */}
      <Paper
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          borderRadius: 0,
        }}
      >
        <Typography variant="h6">Design Comparison</Typography>
        <ToggleButtonGroup
          value={selectedTheme}
          exclusive
          onChange={(e, val) => val && setSelectedTheme(val)}
          size="small"
        >
          <ToggleButton value="current">Current</ToggleButton>
          <ToggleButton value="dense">A: Professional Dense</ToggleButton>
          <ToggleButton value="clinical">B: Clinical Clean</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Viewing: <strong>{selected.label}</strong>
        </Typography>
      </Paper>

      {/* Preview area */}
      <ThemeProvider theme={selected.theme}>
        <CssBaseline />
        <Box display="flex" sx={{ minHeight: 'calc(100vh - 80px)' }}>
          {/* Navigation preview */}
          <NavigationComparison variant={selected.navVariant} />

          {/* Main content preview */}
          <Box sx={{ flexGrow: 1, p: 3, bgcolor: selected.theme.palette.background.default }}>
            <selected.Component />
          </Box>
        </Box>
      </ThemeProvider>

      {/* Design notes */}
      <Paper sx={{ m: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>Design Notes</Typography>

        {selectedTheme === 'current' && (
          <Box>
            <Typography variant="subtitle2" color="error.main" gutterBottom>Issues with Current Design:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Large, blocky form fields with excessive padding</li>
              <li>Rainbow of chip colors (success, warning, error, primary, info) creates visual noise</li>
              <li>Collapsible accordions hide information by default</li>
              <li>Low information density - too much whitespace</li>
              <li>Generic MUI icons lack professional refinement</li>
              <li>Default Roboto font at large sizes feels consumer-grade</li>
            </ul>
          </Box>
        )}

        {selectedTheme === 'dense' && (
          <Box>
            <Typography variant="subtitle2" color="primary" gutterBottom>Option A: Professional Dense</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Typography:</strong> Inter font, smaller base size (13px), tighter leading</li>
              <li><strong>Density:</strong> Compact tables, smaller form fields, reduced padding</li>
              <li><strong>Colors:</strong> Muted blue primary, slate grays, minimal color variation</li>
              <li><strong>Status indicators:</strong> Small colored dots instead of colorful chips</li>
              <li><strong>Sections:</strong> Always expanded, separated by subtle dividers</li>
              <li><strong>Navigation:</strong> Smaller icons, tighter spacing, subtle selection states</li>
              <li><strong>Inspired by:</strong> Linear, Notion, modern SaaS admin panels</li>
            </ul>
          </Box>
        )}

        {selectedTheme === 'clinical' && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#0f766e' }} gutterBottom>Option B: Clinical Clean</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>Typography:</strong> IBM Plex Sans, clean medical/technical feel</li>
              <li><strong>Density:</strong> Card-based list view, inline label-value pairs</li>
              <li><strong>Colors:</strong> Teal primary (healthcare association), minimal accents</li>
              <li><strong>Status indicators:</strong> Chip toggles, small status dots</li>
              <li><strong>Sections:</strong> Flat layout, label-value grids for forms</li>
              <li><strong>Navigation:</strong> Text-only (no icons), left border selection indicator</li>
              <li><strong>Inspired by:</strong> Epic/Cerner EMR, clinical research platforms</li>
            </ul>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default DesignCompare
